import isNil from "lodash.isnil";
import { X } from "lucide-react";
import React, { ReactNode, useId } from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Stack,
  SxProps,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { Breakpoint } from "@mui/system";

import { IgButton } from "./IgButton";
import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";

const PREFIX = "IgDialog";

const classes = {
  dialogTitle: `${PREFIX}-dialogTitle`,
};

const StyledDialog = styled(Dialog)(() => ({
  [`& .${classes.dialogTitle}`]: {
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "unset",
  },
}));

interface BaseDialogProps extends Omit<
  DialogProps,
  "open" | "title" | "content"
> {
  showDialog: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  content?: ReactNode;
  rawContent?: ReactNode;
  yesButtonText?: ReactNode;
  noButtonText?: string | null;
  handleYesClick?: () => void;
  handleNoClick?: () => void;
  yesButtonDisabled?: boolean;
  handleClose?: (reason: "backdropClick" | "escapeKeyDown" | "button") => void;
  headerEndAdornment?: ReactNode;
  loading?: boolean;
  shouldNotClose?: boolean;
  dialogTitleWidth?: string;
  padding?: string;
  dialogContentWidth?: string;
  hideActions?: boolean;
  minWidth?: string;
  maxWidth?: Breakpoint | false;
  dialogHeight?: string | null;
  showCloseIcon?: boolean;
  subtitleSx?: SxProps;
  contentSx?: SxProps;
  dialogImage?: ReactNode;
  dontUseDialogContent?: boolean;
  buttonVariant?: "text" | "outlined" | "contained";
  yesButtonColor?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  dialogContentPadding?: string;
  learnMoreButton?: ReactNode;
  yesButtonDisabledTooltip?: string;
}

export const IgDialog = ({
  showDialog,
  handleClose,
  handleNoClick,
  title,
  yesButtonText,
  yesButtonDisabled,
  handleYesClick,
  headerEndAdornment,
  noButtonText,
  loading,
  content,
  rawContent,
  maxWidth,
  minWidth,
  shouldNotClose,
  dialogTitleWidth,
  dialogContentWidth,
  dialogHeight,
  subtitle,
  showCloseIcon,
  hideActions,
  subtitleSx,
  padding,
  contentSx,
  dialogImage,
  dontUseDialogContent,
  buttonVariant,
  yesButtonColor,
  dialogContentPadding,
  learnMoreButton,
  yesButtonDisabledTooltip,
  ...rest
}: BaseDialogProps) => {
  const theme = useTheme();
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const subtitleId = `${generatedId}-subtitle`;

  const onYesClickPress = () => {
    if (handleYesClick) {
      handleYesClick();
    }
    if (!shouldNotClose) {
      handleClose?.("button");
    }
  };

  const onNoClickPress = () => {
    if (handleNoClick) {
      handleNoClick();
    } else {
      handleClose?.("button");
    }
  };

  return (
    <StyledDialog
      open={showDialog}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={subtitle ? subtitleId : undefined}
      onClose={(_ev, reason) => handleClose?.(reason)}
      maxWidth={maxWidth || "xs"}
      scroll={"body"}
      slotProps={{
        paper: {
          sx: {
            width: maxWidth ? undefined : "460px",
            height: isNil(dialogHeight) ? undefined : dialogHeight || "275px",
            justifyContent: "center",
            minWidth: minWidth || "500px",
          },
        },
      }}
      {...rest}
    >
      {showCloseIcon && (
        <Box
          sx={{
            position: "absolute",
            left: "calc(100% - 56px)",
            top: "12px",
          }}
        >
          <IconButton
            aria-label="close"
            disabled={loading}
            onClick={() => handleClose?.("button")}
          >
            <X size={24} />
          </IconButton>
        </Box>
      )}
      <Stack
        direction={"column"}
        spacing={4}
        sx={{
          padding: padding || "24px 24px 24px 24px",
          minWidth: minWidth || undefined,
        }}
      >
        {title && (
          <DialogTitle
            sx={{
              width: dialogTitleWidth || "90%",
              padding: "0 0 4px 0",
            }}
            id="dialogBox-title"
            alignItems={"flex-start"}
            className={classes.dialogTitle}
          >
            <Grid
              container
              sx={{ flexWrap: "nowrap" }}
              alignItems={"flex-start"}
              justifyContent={"space-between"}
              columnSpacing={2}
            >
              <Grid container direction={"column"} rowSpacing={1}>
                {dialogImage && (
                  <Grid>
                    <>{dialogImage}</>
                  </Grid>
                )}
                <Grid container justifyContent={"flex-start"}>
                  <IgTypography id={titleId} variant={"h3"} fontWeight={600}>
                    {title}
                  </IgTypography>
                </Grid>
              </Grid>
              {headerEndAdornment && (
                <Grid sx={{ marginLeft: "auto" }}>{headerEndAdornment}</Grid>
              )}
            </Grid>
          </DialogTitle>
        )}
        {subtitle && (
          <IgTypography
            id={subtitleId}
            color={theme.palette.text.secondary}
            sx={{ ...subtitleSx }}
          >
            <>{subtitle}</>
          </IgTypography>
        )}
        {content && (
          <DialogContent
            sx={{
              width: dialogContentWidth || "500px",
              padding: "24px 0 0 0",
              ...contentSx,
            }}
          >
            {content}
          </DialogContent>
        )}
        {rawContent && dontUseDialogContent ? (
          rawContent
        ) : rawContent ? (
          <DialogContent
            sx={{
              width: dialogContentWidth || "100%",
              padding: "24px 0 0 0 !important",
            }}
          >
            {rawContent}
          </DialogContent>
        ) : null}
        {hideActions ? null : (
          <DialogActions sx={{ padding: "24px 0 0 0", width: "100%" }}>
            <Grid
              container
              direction={"row"}
              justifyContent={"space-between"}
              wrap={"nowrap"}
              size={12}
              sx={{ width: "100%" }}
            >
              {learnMoreButton && <Grid size={"auto"}>{learnMoreButton}</Grid>}
              <Grid
                container
                justifyContent="end"
                direction={"row"}
                columnSpacing={1}
                size={12}
              >
                {noButtonText !== null && (
                  <Grid sx={{ marginRight: "10px" }}>
                    <IgButton
                      size={"small"}
                      variant={buttonVariant ?? "contained"}
                      color={"secondary"}
                      disabled={loading}
                      onClick={() => {
                        onNoClickPress();
                      }}
                    >
                      {noButtonText || "Cancel"}
                    </IgButton>
                  </Grid>
                )}
                {yesButtonText !== null && (
                  <Grid>
                    <IgTooltip
                      title={
                        yesButtonDisabled && yesButtonDisabledTooltip
                          ? yesButtonDisabledTooltip
                          : ""
                      }
                    >
                      <IgButton
                        size={"small"}
                        disabled={yesButtonDisabled}
                        loading={loading}
                        onClick={onYesClickPress}
                        color={yesButtonColor}
                      >
                        {yesButtonText || "Yes, discard my changes"}
                      </IgButton>
                    </IgTooltip>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </DialogActions>
        )}
      </Stack>
    </StyledDialog>
  );
};
